"use client";

import { Suspense } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import TerminalButton from "@/components/terminal-button";
import SmtpButton from "@/components/smtp-button";
import UserMenu from "@/components/user-menu";
import NavLinks from "@/components/nav-links";
import MarketIndices from "@/components/market-indices";
import ContentShell from "@/components/content-shell";
import { useAuth } from "@/lib/auth-context";

/** The desktop nav, indices pane and footer. The `/m` tree is a separate
 *  mobile site with its own chrome, so it renders bare — but it still sits
 *  inside the root layout's providers, which is where auth and toasts live. */
export default function DesktopChrome({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { impersonator, user } = useAuth();
  if (pathname === "/m" || pathname.startsWith("/m/")) return <>{children}</>;

  const impersonating = !!impersonator;

  return (
    <>
      {/* Fixed red frame while an admin is viewing as another user. */}
      {impersonating && (
        <div
          className="pointer-events-none fixed inset-0 z-[100] border-4 border-red"
          aria-hidden
        />
      )}
      <nav className={`fixed top-0 left-0 right-0 h-[50px] flex items-center justify-between px-5 bg-surface border-b z-40 ${
        impersonating ? "border-red" : "border-border"
      }`}>
        <div className="flex items-center gap-6">
          <Link href="/accounts" className="text-lg font-bold tracking-wide text-purple hover:text-purple/80 no-underline">Trader</Link>
          <NavLinks />
        </div>
        <div className="flex items-center gap-4">
          {impersonating && (
            <span className="text-[11px] font-medium text-red bg-red/10 px-2 py-0.5 rounded border border-red/40">
              Viewing as {user}
            </span>
          )}
          <SmtpButton />
          <TerminalButton />
          <UserMenu />
        </div>
      </nav>
      <Suspense fallback={null}>
        <MarketIndices />
      </Suspense>
      <ContentShell>{children}</ContentShell>
      <footer className={`fixed bottom-0 left-0 right-0 h-[50px] flex items-center justify-center bg-surface border-t text-xs ${
        impersonating ? "border-red text-red" : "border-border text-muted"
      }`}>
        {impersonating
          ? <>Viewing as <span className="font-semibold mx-1">{user}</span> · admin {impersonator}</>
          : <>Copyright &copy; 2026 by Mark Malson</>}
      </footer>
    </>
  );
}
